import axios from 'axios';
import toast from 'react-hot-toast';

/**
 * Enhanced API Client with better error handling, retry logic, and configuration management
 */
class ApiClient {
  constructor(config = {}) {
    this.config = {
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000',
      timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      ...config
    };

    this.instance = this.createAxiosInstance();
    this.interceptors = this.setupInterceptors();
  }

  createAxiosInstance() {
    return axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
  }

  setupInterceptors() {
    // Request interceptor
    const requestInterceptor = this.instance.interceptors.request.use(
      (config) => {
        // Add auth token
        const token = localStorage.getItem('rdx_auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add timestamp to prevent caching
        config.headers['X-Requested-At'] = Date.now();

        // Add request ID for tracking
        config.headers['X-Request-ID'] = this.generateRequestId();

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    const responseInterceptor = this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle token expiration
        if (error.response?.status === 401 && !originalRequest._retry) {
          this.handleAuthError();
          return Promise.reject(error);
        }

        // Handle network errors with retry
        if (this.shouldRetry(error, originalRequest)) {
          return this.retryRequest(originalRequest);
        }

        // Handle and display errors
        this.handleApiError(error);
        return Promise.reject(error);
      }
    );

    return { requestInterceptor, responseInterceptor };
  }

  shouldRetry(error, originalRequest) {
    return (
      !originalRequest._retry &&
      originalRequest._retryCount < this.config.retryAttempts &&
      (error.code === 'ECONNABORTED' || 
       error.code === 'NETWORK_ERROR' ||
       (error.response?.status >= 500 && error.response?.status < 600))
    );
  }

  async retryRequest(originalRequest) {
    originalRequest._retry = true;
    originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

    // Exponential backoff
    const delay = this.config.retryDelay * Math.pow(2, originalRequest._retryCount - 1);
    await new Promise(resolve => setTimeout(resolve, delay));

    return this.instance(originalRequest);
  }

  handleAuthError() {
    localStorage.removeItem('rdx_auth_token');
    localStorage.removeItem('rdx_user');
    
    // Only redirect if not already on login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
      toast.error('Session expired. Please login again.');
    }
  }

  handleApiError(error) {
    if (error.response) {
      this.handleHttpError(error.response);
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout. Please check your connection.');
    } else if (error.message === 'Network Error') {
      toast.error('Network error. Please check your connection.');
    } else {
      toast.error('Something went wrong. Please try again.');
    }
  }

  handleHttpError(response) {
    const { status, data } = response;

    switch (status) {
      case 400:
        toast.error(data?.message || 'Bad request');
        break;
      case 403:
        toast.error("You don't have permission to perform this action");
        break;
      case 404:
        toast.error('Resource not found');
        break;
      case 422:
        this.handleValidationError(data);
        break;
      case 429:
        toast.error('Too many requests. Please try again later.');
        break;
      case 500:
        toast.error('Internal server error. Please try again later.');
        break;
      case 503:
        toast.error('Service temporarily unavailable. Please try again later.');
        break;
      default:
        toast.error(data?.message || 'An error occurred');
    }
  }

  handleValidationError(data) {
    if (data?.detail && Array.isArray(data.detail)) {
      data.detail.forEach(error => {
        const field = error.loc?.[error.loc.length - 1] || 'Field';
        toast.error(`${field}: ${error.msg}`);
      });
    } else if (data?.errors) {
      Object.keys(data.errors).forEach(field => {
        data.errors[field].forEach(message => {
          toast.error(`${field}: ${message}`);
        });
      });
    } else {
      toast.error(data?.message || 'Validation error');
    }
  }

  generateRequestId() {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // HTTP Methods with enhanced functionality
  async get(url, config = {}) {
    const response = await this.instance.get(url, config);
    return response.data;
  }

  async post(url, data = {}, config = {}) {
    const response = await this.instance.post(url, data, config);
    return response.data;
  }

  async put(url, data = {}, config = {}) {
    const response = await this.instance.put(url, data, config);
    return response.data;
  }

  async patch(url, data = {}, config = {}) {
    const response = await this.instance.patch(url, data, config);
    return response.data;
  }

  async delete(url, config = {}) {
    const response = await this.instance.delete(url, config);
    return response.data;
  }

  // Specialized methods
  async upload(url, formData, onUploadProgress = null) {
    const config = {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress
    };
    return this.post(url, formData, config);
  }

  async download(url, filename, config = {}) {
    const response = await this.instance.get(url, {
      ...config,
      responseType: 'blob'
    });

    // Create and trigger download
    const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return response.data;
  }

  // Advanced query building
  buildQuery(params = {}) {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(item => queryParams.append(`${key}[]`, item));
        } else {
          queryParams.append(key, value);
        }
      }
    });

    return queryParams.toString();
  }

  // Paginated requests
  async getPaginated(url, params = {}) {
    const { page = 1, limit = 20, ...filters } = params;
    const skip = (page - 1) * limit;
    
    const queryParams = this.buildQuery({ skip, limit, ...filters });
    const finalUrl = queryParams ? `${url}?${queryParams}` : url;
    
    return this.get(finalUrl);
  }

  // Batch requests
  async batch(requests) {
    try {
      const promises = requests.map(({ method, url, data, config }) => {
        return this[method.toLowerCase()](url, data, config);
      });
      
      return await Promise.allSettled(promises);
    } catch (error) {
      console.error('Batch request failed:', error);
      throw error;
    }
  }

  // Health check
  async healthCheck() {
    try {
      await this.get('/health');
      return true;
    } catch {
      return false;
    }
  }

  // Cleanup method
  destroy() {
    this.instance.interceptors.request.eject(this.interceptors.requestInterceptor);
    this.instance.interceptors.response.eject(this.interceptors.responseInterceptor);
  }
}

// Create singleton instance
export const apiClient = new ApiClient();

// Export class for custom instances
export default ApiClient;